import { useRecoilState, useResetRecoilState } from "recoil";
import { useEffect, useRef } from "react";
import {
  userState,
  authLoadingState,
  authErrorState,
  userInfoLoadedState,
} from "../recoil/auth";
import {
  loginAPI,
  signupAPI,
  getUserInfoAPI,
  getUserDetailAPI,
  getPriceGradeAPI,
  getPriceGradeDetailAPI,
  logoutAPI,
} from "../api/index";
import { safeStorage } from "../utils/storage";

export const useAuth = () => {
  const [user, setUser] = useRecoilState(userState);
  const [loading, setLoading] = useRecoilState(authLoadingState);
  const [error, setError] = useRecoilState(authErrorState);
  const [userInfoLoaded, setUserInfoLoaded] =
    useRecoilState(userInfoLoadedState);

  // Recoil 상태 리셋 함수들
  const resetUserState = useResetRecoilState(userState);
  const resetAuthLoadingState = useResetRecoilState(authLoadingState);
  const resetAuthErrorState = useResetRecoilState(authErrorState);
  const resetUserInfoLoadedState = useResetRecoilState(userInfoLoadedState);

  const hasRestoredPriceInfo = useRef(false);
  const isRestoring = useRef(false);

  // 모든 저장소 완전 정리 함수
  const clearAllAuthData = () => {
    // safeStorage를 사용하여 모든 저장소에서 인증 관련 데이터 제거
    const authKeys = ["user", "token", "refresh_token", "userInfoLoaded"];

    authKeys.forEach((key) => {
      safeStorage.removeItem(key);
    });

    // Recoil 관련 키들도 제거
    const recoilKeys = [
      "recoil-persist-user",
      "recoil-persist-userInfoLoaded",
      "zoomSettings",
      "zoomNotification",
    ];
    recoilKeys.forEach((key) => {
      safeStorage.removeItem(key);
    });

    // sessionStorage와 localStorage에서 직접 제거 (이중 보호)
    try {
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refresh_token");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("userInfoLoaded");
      sessionStorage.removeItem("zoomSettings");
      sessionStorage.removeItem("zoomNotification");
    } catch (e) {
      console.warn("sessionStorage 정리 실패:", e);
    }

    try {
      localStorage.removeItem("token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      localStorage.removeItem("userInfoLoaded");
      localStorage.removeItem("zoomSettings");
      localStorage.removeItem("zoomNotification");
    } catch (e) {
      console.warn("localStorage 정리 실패:", e);
    }

    // 메모리 저장소도 정리
    if (window._tempStorage) {
      authKeys.forEach((key) => {
        delete window._tempStorage[key];
      });
      recoilKeys.forEach((key) => {
        delete window._tempStorage[key];
      });
    }

    // 브라우저에서 직접 확인할 수 있도록 전역 변수로 설정
    window.clearedAuthKeys = authKeys;
    window.remainingKeys = {
      sessionStorage: Object.keys(sessionStorage),
      tempStorage: Object.keys(window._tempStorage || {}),
    };
  };

  // 사용자 정보에 price_grade 정보 추가하는 함수
  const addPriceInfoToUser = async (userInfo) => {
    if (!userInfo || !userInfo.price_grade_id) {
      return userInfo;
    }

    try {
      const priceGradeResponse = await getPriceGradeAPI(
        userInfo.price_grade_id
      );

      const priceGradeDetailResponse = await getPriceGradeDetailAPI(
        userInfo.price_grade_id
      );

      const result = {
        ...userInfo,
        price_grade: priceGradeResponse.data,
        price_grade_detail: priceGradeDetailResponse.data,
      };

      return result;
    } catch (priceErr) {
      console.error("price_grade 정보 가져오기 실패:", priceErr);
      return userInfo;
    }
  };

  // 새로고침 시 회원정보 복원 (한 번만 실행)
  useEffect(() => {
    const restoreUserInfo = async () => {
      // 저장소 가용성 체크
      try {
        safeStorage.setItem("test", "test");
        safeStorage.removeItem("test");
      } catch (e) {
        console.error("모든 저장소를 사용할 수 없습니다:", e);
        return;
      }

      // 토큰이 없으면 실행하지 않음
      if (!safeStorage.getItem("token")) {
        return;
      }

      // 이미 복원 중이거나 로그인 중이면 실행하지 않음 (중복 방지)
      if (isRestoring.current) {
        return;
      }

      isRestoring.current = true;

      try {
        // safeStorage에서 기본 사용자 정보 확인
        const storedUser = safeStorage.getItem("user");
        if (storedUser) {
          let parsedUser;
          try {
            parsedUser = JSON.parse(storedUser);
          } catch (parseError) {
            console.error("사용자 정보 파싱 실패:", parseError);
            safeStorage.removeItem("user");
            return;
          }

          // 기본 정보가 있으면 상세 정보 다시 가져오기
          if (parsedUser.username && parsedUser.role) {
            const userInfoResponse = await getUserDetailAPI(
              parsedUser.username,
              parsedUser.role
            );

            if (
              userInfoResponse.data?.list &&
              userInfoResponse.data.list.length > 0
            ) {
              const userInfo = userInfoResponse.data.list[0];
              const userWithPriceInfo = await addPriceInfoToUser(userInfo);
              setUser(userWithPriceInfo);
              // 사용자 정보 로딩 완료 상태 설정
              setUserInfoLoaded(true);
            } else {
              setUser(parsedUser);
            }
          }
        }
      } catch (error) {
        console.error("회원정보 복원 실패:", error);
        // 복원 실패 시 저장된 정보라도 사용
        const storedUser = sessionStorage.getItem("user");
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }
      } finally {
        isRestoring.current = false;
      }
    };

    restoreUserInfo();
  }, []); // 빈 배열로 변경하여 컴포넌트 마운트 시 한 번만 실행

  // 로그인
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      // 로그인 전 기존 세션과 토큰 완전 정리
      // 1. Recoil 상태 리셋
      resetUserState();
      resetUserInfoLoadedState();
      resetAuthErrorState();
      resetAuthLoadingState();

      // 2. 모든 저장소에서 인증 관련 데이터 완전 제거
      clearAllAuthData();

      // 3. restoreUserInfo가 실행되지 않도록 플래그 설정
      isRestoring.current = true;

      // 4. 약간의 지연을 두어 저장소 정리가 완전히 완료되도록 함
      await new Promise((resolve) => setTimeout(resolve, 50));

      const response = await loginAPI(credentials);

      // 토큰 저장
      if (response.data?.token) {
        try {
          safeStorage.setItem("token", response.data.token);
          if (response.data.refresh_token) {
            safeStorage.setItem("refresh_token", response.data.refresh_token);
          }
        } catch (storageError) {
          console.error("토큰 저장 실패:", storageError);
          throw new Error(
            "브라우저 저장소에 접근할 수 없습니다. 프라이빗 모드를 해제하거나 다른 브라우저를 사용해주세요."
          );
        }
      }

      // 사용자 정보 가져오기
      if (response.data?.user?.username && response.data?.user?.role) {
        try {
          const userInfoResponse = await getUserDetailAPI(
            response.data.user.username,
            response.data.user.role
          );

          if (
            userInfoResponse.data?.list &&
            userInfoResponse.data.list.length > 0
          ) {
            // list 배열의 0번째 사용자 정보 저장
            const userInfo = userInfoResponse.data.list[0];

            // price_grade와 price_grade_detail 정보 가져오기
            const userWithPriceInfo = await addPriceInfoToUser(userInfo);
            setUser(userWithPriceInfo);
            // 사용자 정보 로딩 완료 상태 설정
            setUserInfoLoaded(true);
          } else {
            // getUserDetailAPI에서 정보를 가져올 수 없는 경우 기본 정보 사용
            setUser(response.data.user);
          }
        } catch (userInfoErr) {
          console.error("사용자 정보 가져오기 실패:", userInfoErr);
          // getUserDetailAPI 실패 시 기본 정보 사용
          setUser(response.data.user);
        }
      } else {
        setUser(response.data.user);
      }

      // 로그인 성공 후 restoreUserInfo 플래그 해제
      isRestoring.current = false;

      return { success: true }; // 로그인 성공 반환
    } catch (err) {
      // 에러 발생 시에도 플래그 해제
      isRestoring.current = false;
      setError(err.response?.data?.message || "로그인에 실패했습니다.");
      return { success: false }; // 로그인 실패 반환
    } finally {
      setLoading(false);
    }
  };

  // 회원가입 (현재 사용되지 않음)
  // const signup = async (credentials) => {
  //   console.log("signup", credentials);
  //   try {
  //     setLoading(true);
  //     setError(null);

  //     const result = await signupAPI(credentials);
  //     console.log("signup result", result);

  //     if (result === "") {
  //       // 회원가입 성공 (빈 문자열)
  //       console.log("회원가입 성공");
  //       return { success: true };
  //     } else {
  //       // 회원가입 실패 (에러 메시지)
  //       setError(result);
  //       return { success: false, message: result };
  //     }
  //   } catch (err) {
  //     const errorMessage =
  //       err.response?.data?.message || "회원가입에 실패했습니다.";
  //     setError(errorMessage);
  //     return { success: false, message: errorMessage };
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const logout = async () => {
    try {
      // 현재 사용자 정보로 로그아웃 API 호출
      if (user && user.id) {
        await logoutAPI("MEMBER", user.id);
      }
    } catch (err) {
      console.error("Logout API error:", err);
      // API 호출 실패해도 로컬 정리는 진행
    } finally {
      // Recoil 상태 완전 리셋 (sessionStorageEffect와 함께 작동)
      resetUserState();
      resetAuthLoadingState();
      resetAuthErrorState();
      resetUserInfoLoadedState();

      // 추가로 직접 null 설정 (이중 보호)
      setUser(null);
      setError(null);
      setLoading(false);
      setUserInfoLoaded(false);

      // sessionStorage에서 모든 인증 관련 데이터 제거
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("refresh_token");
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("userInfoLoaded");

      // sessionStorage 완전 정리
      clearAllAuthData();

      // 저장된 자격증명 삭제
      localStorage.removeItem("savedUsername"); // 기존 호환성 유지
      // 새로운 암호화된 자격증명 삭제
      try {
        const { clearCredentials } = require("../utils/encryption");
        clearCredentials();
      } catch (error) {
        console.error("자격증명 삭제 실패:", error);
      }

      // 브라우저에서 직접 확인할 수 있도록 전역 변수로 설정
      window.lastLogoutTime = new Date().toISOString();
      window.sessionStorageAfterLogout = Object.keys(sessionStorage);

      // // 모든 환경에서 로그인 페이지로 직접 이동 (캐시 완전 우회)
      // setTimeout(() => {
      //   // 타임스탬프를 쿼리 파라미터로 추가하여 캐시 버스팅
      //   const timestamp = Date.now();
      //   // 완전히 새로운 페이지 로드를 강제 (캐시 완전 우회)
      //   // replace를 사용하여 히스토리에 남기지 않고, 쿼리 파라미터로 캐시 방지
      //   window.location.replace(`/login?_t=${timestamp}&_nocache=1`);
      // }, 100);
    }
  };

  const getUserInfo = async (username) => {
    try {
      setLoading(true);
      setError(null);

      const result = await getUserInfoAPI(username);

      if (result === "") {
        // 중복되지 않은 아이디 (사용 가능)
        return { success: true, isAvailable: true };
      } else {
        // 중복된 아이디 또는 오류
        setError(result);
        return { success: false, message: result, isAvailable: false };
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "중복 확인 중 오류가 발생했습니다.";
      setError(errorMessage);
      return { success: false, message: errorMessage, isAvailable: false };
    } finally {
      setLoading(false);
    }
  };

  // 회원 정보 업데이트 함수
  const updateUserInfo = async () => {
    if (!user?.username || !user?.role) {
      return { success: false, message: "사용자 정보가 없습니다." };
    }

    try {
      const userInfoResponse = await getUserDetailAPI(user.username, user.role);

      if (
        userInfoResponse.data?.list &&
        userInfoResponse.data.list.length > 0
      ) {
        const userInfo = userInfoResponse.data.list[0];

        // price_grade와 price_grade_detail 정보 추가
        const userWithPriceInfo = await addPriceInfoToUser(userInfo);

        // Recoil 상태 업데이트
        setUser(userWithPriceInfo);

        return { success: true, userInfo: userWithPriceInfo };
      } else {
        console.error("회원 정보를 가져올 수 없습니다.");
        return { success: false, message: "회원 정보를 가져올 수 없습니다." };
      }
    } catch (error) {
      console.error("회원 정보 업데이트 실패:", error);
      return {
        success: false,
        message:
          error.response?.data?.message || "회원 정보 업데이트에 실패했습니다.",
      };
    }
  };

  return {
    user,
    loading,
    error,
    login,
    logout,
    getUserInfo,
    updateUserInfo,
    isAuthenticated: !!user,
  };
};

export default useAuth;
